import { screen } from '@testing-library/react'
import { Address } from './address'
import { Pharmacy } from './indication'
import { BACK_TYPE, Step, STEP_TYPE } from './step'

export enum CONSULTATION_TYPE {
  MEDICAL = 'medicalConsultation',
  PRESCRIPTION = 'prescriptionRequest',
  VIDEO = 'videoConsultation',
  VOICE = 'voiceConsultation',
  MENTAL = 'mentalHealthEvaluation'
}

export enum OTHER_SERVICE {
  HEALTH_NAVIGATOR = 'healthNavigator'
}

export type AVAILABLE_SERVICE = CONSULTATION_TYPE | OTHER_SERVICE

export const SERVICES_ORDER: AVAILABLE_SERVICE[] = [
  OTHER_SERVICE.HEALTH_NAVIGATOR,
  CONSULTATION_TYPE.MEDICAL,
  CONSULTATION_TYPE.VIDEO,
  CONSULTATION_TYPE.PRESCRIPTION,
  CONSULTATION_TYPE.MENTAL,
  CONSULTATION_TYPE.VOICE
]

export enum CONSULTATION_STEPS {
  LANDING = 'landing',
  FORM = 'form',
  MATCHING = 'matching',
  MATCH = 'match',
  ANSWER = 'answer',
  INDICATION_SURVEY = 'indicationSurvey',
  ONGOING_VIDEO_CALL = 'ongoingVideoCall',
  VIDEO_CALL_ENDED = 'videoCallEnded',
  WAITING_FOR_INFO = 'waitingForInfo',
  WAITING_FOR_SWAP = 'waitingForSwap',
  WAITING_FOR_CONSULTATION_PAYMENT = 'waitingForConsultationPayment',
  MENTAL_HEALTH_DISCLAIMER = 'mentalHealthDisclaimer'
}

export enum IndicationFileStatus {
  CREATING = 'CREATING',
  CREATED = 'CREATED',
  ERROR = 'ERROR'
}

export enum CONTENT_TYPE {
  CONSULTATION_NOTES = 'ConsultationNotes',
  PRESCRIPTION = 'Prescription',
  PRESCRIPTION_DECISIONS = 'PrescriptionDecisions',
  SICK_CERTIFICATE = 'SickCertificate',
  REFERRAL = 'Referral',
  DOCTORS_ASSESSMENT = 'DoctorsAssessment'
}

export type CaseConsultationFile =
  | {
      status: IndicationFileStatus.CREATING
      content?: CONTENT_TYPE[]
    }
  | {
      status: IndicationFileStatus.CREATED
      url: string
      content?: CONTENT_TYPE[]
    }
  | {
      status: IndicationFileStatus.ERROR
      message: string
      content?: CONTENT_TYPE[]
    }

/** @deprecated Use CaseConsultationFile. */
export type IndicationFiles = CaseConsultationFile

export type License = {
  licenseNumber: string
  countries: string[]
}

export type Professional = {
  name: string
  title: string
  specialties: string[]
  licenses: License[]
  profileUrl: string
  gender: string
  profileImg: string
}

export type Question = {
  createdAt?: number // timestamp
  question: string
  attachments: string[] // Urls to bucket

  // If answered ↓
  professional?: Professional
  answer?: string
}

export enum DELIVERY_ADDRESS_STATUS {
  PENDING = 'pending',
  REJECTED = 'rejected',
  APPROVED = 'approved'
}

export enum PRESCRIPTION_STATUS {
  WAITING_FOR_FULFILLMENT = 'waitingForFulfillment',
  ADDRESS_REJECTED = 'addressRejected',
  WAITING_FOR_PAYMENT = 'waitingForPayment',
  PAYMENT_REJECTED = 'paymentRejected',
  RESOLVED = 'resolved',
  ERROR = 'error',

  PROCESSING = 'processing',
  PHARMACY_ASKED = 'pharmacyAsked',
  PATIENT_ADDRESS_ASKED = 'patientAddressAsked',
  CREATED = 'created',
  DELIVERY_ADDRESS_ASKED = 'deliveryAddressAsked'
}

export type PrescriptionPayment = {
  paymentUrl: string
}

export enum PRESCRIPTION_FULFILLMENT_OPTION {
  DELIVERY = 'delivery',
  COLLECT = 'collect',
  USER_ADDRESS = 'userAddress',
  PDF = 'pdf'
}

export enum PRESCRIPTION_INTEGRATION {
  HEALTHMAIL = 'healthmail',
  HEALTHMAIL_ILH = 'healthmail-ilh',
  HEALTHMAIL_DOUGLASDALE = 'healthmail-douglasdale',
  HIN = 'hin',
  DOSE_SPOT = 'doseSpot',
  THREE_TREE = '3Tree',
  SIGNATURERX = 'signaturerx',
  DEFAULT = 'default'
}

export interface PrescriptionData {
  duration: string
  instructions: string
  quantity: string
  medication: string
  frequency: string
}

export type Prescription = {
  isRefused: boolean
  integration?: PRESCRIPTION_INTEGRATION
  deliveryAddress?: Address
  fulfillmentOptions?: PRESCRIPTION_FULFILLMENT_OPTION[]
  status: PRESCRIPTION_STATUS
  information?: {
    pharmacy?: Pharmacy
    payment?: PrescriptionPayment
  }

  prescriptions: {
    message?: string
    status: string
    prescriptionData?: PrescriptionData
  }[]
}

export enum CONSULTATION_STATUS {
  NEW = 'new',
  OPEN = 'open',
  CLAIMED = 'claimed',
  RESOLVED = 'resolved',
  SUBMITTED = 'submitted',
  CANCELED = 'canceled',
  CLOSED = 'closed',
  CLOSED_WORATING = 'closedworating',
  CLOSED_WITH_REPORT_ACCESS = 'closedWithReportAccess',
  REPORT_ACCESS_EXPIRED = 'reportAccessExpired',
  WAITING_FOR_RATING = 'waitingforrating',
  ONGOING_VIDEO_CALL = 'onGoingVideoCall',
  VIDEO_CALL_ENDED = 'videoCallEnded',
  WAITING_FOR_INFO = 'waitingForInfo',
  WAITING_FOR_SWAP = 'waitingForSwap',
  WAITING_FOR_CONSULTATION_PAYMENT = 'waitingForConsultationPayment'
}

type PaymentInformation = {
  userIsRequiredToPay: boolean
  url: string | undefined
  currency: string
  price: number
}

export type MedicalAdvice = {
  therapy?: string
  therapyKey?: string
  referral: string
  referralKey: string
  outcome: string
  category: number
  isUrgencyViewed: boolean
  isRegistered: boolean
}

export type SickNote = {
  reason?: string
  startDate: string
  endDate: string
}

export type Referral = {
  questionId: string
  content: {
    specialty: string
    clinic: string
    preferredConsultant?: string
    priority: string
    reason: string
    relevantInformation?: string
    urgency?: string
  }
}

export type VideoConsultation = {
  uuid: number
  appId: string
  token: string
  room: string
  provider: string
  patientStartedAt?: string
  patientJoinedAt?: string
  patientHungUpAt?: string
  isRejoin: boolean
  isRecall: boolean
}

export type Consultation = {
  consultationId: string
  status: CONSULTATION_STATUS
  userId: string
  questions?: Question[] // Up to 3
  professional?: Professional
  consultationType: CONSULTATION_TYPE
  prescription?: Prescription
  caseConsultationPdf?: CaseConsultationFile
  video?: VideoConsultation
  notify?: boolean
  medicalAdvice?: MedicalAdvice
  paymentInformation?: PaymentInformation
  sickNotes?: SickNote[]
  referrals?: Referral[]
  createdAt: string
  rating?: number | null
}

export const CONSULTATION_LANDING_STEP: Step = {
  id: 'consultation-landing',
  name: 'consultation-landing',
  type: STEP_TYPE.CONSULTATION,
  components: {},
  componentsOrder: [],
  backButton: BACK_TYPE.SHORT
}
